import type { ArticleEditor } from "@/core/services/types/article.types";
import type {
  ActivitySchedule,
  ActivityTypeValue,
  EventCategoryValue,
  EventEditor,
} from "@/core/services/types/event.types";
import {
  normalizePonctuelSchedule,
  normalizeRecurrentSchedule,
  parseActivityScheduleFromDb,
} from "@/core/services/types/event.types";
import { eventIsoToCalendarDay } from "@/lib/event-dates";
import type { PlaceEditor } from "@/core/services/types/place.types";

function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function nullableRichText(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "<p></p>") return null;
  return value;
}

function nullableWebsiteUrl(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function sameNullableText(current: string, saved: string | null | undefined): boolean {
  return nullableText(current) === nullableText(saved ?? "");
}

function sameNullableRichText(
  current: string,
  saved: string | null | undefined,
): boolean {
  return nullableRichText(current) === nullableRichText(saved ?? "");
}

function sameActivitySchedule(a: ActivitySchedule, b: ActivitySchedule): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function normalizeCurrentActivitySchedule(
  isRecurrent: boolean,
  schedule: ActivitySchedule,
): ActivitySchedule {
  if (isRecurrent && "reccurence" in schedule) {
    return normalizeRecurrentSchedule(schedule.reccurence, schedule.days);
  }

  if (!isRecurrent && "dates" in schedule) {
    return normalizePonctuelSchedule(schedule.dates);
  }

  return isRecurrent ? { reccurence: "Chaque semaine", days: [] } : { dates: [] };
}

export function isArticleContentDirty(
  article: ArticleEditor,
  values: {
    title: string;
    subTitle: string;
    contentHtml: string;
  },
): boolean {
  if (values.title !== article.title) return true;
  if (!sameNullableText(values.subTitle, article.sub_title)) return true;
  if (!sameNullableRichText(values.contentHtml, article.content_rich_text)) {
    return true;
  }
  return false;
}

export function isPlaceContentDirty(
  place: PlaceEditor,
  values: {
    name: string;
    address: string;
    city: string;
    zipCode: string;
    departmentName: string;
    websiteUrl: string;
    ticketingUrl: string;
    googlePlaceId: string;
    latitude: number;
    longitude: number;
    placeType: string;
    descriptionShort: string;
    descriptionLong: string;
  },
): boolean {
  if (values.name !== place.name) return true;
  if (!sameNullableText(values.address, place.address)) return true;
  if (!sameNullableText(values.city, place.city)) return true;
  if (!sameNullableText(values.zipCode, place.zip_code)) return true;
  if (!sameNullableText(values.departmentName, place.department_name)) return true;
  if (nullableWebsiteUrl(values.websiteUrl) !== nullableWebsiteUrl(place.website_url ?? "")) {
    return true;
  }
  if (
    nullableWebsiteUrl(values.ticketingUrl) !==
    nullableWebsiteUrl(place.ticketing_url ?? "")
  ) {
    return true;
  }
  if (nullableText(values.googlePlaceId) !== nullableText(place.google_place_id ?? "")) {
    return true;
  }
  if (values.latitude !== place.latitude) return true;
  if (values.longitude !== place.longitude) return true;
  if (nullableText(values.placeType) !== nullableText(place.place_type ?? "")) {
    return true;
  }
  if (!sameNullableText(values.descriptionShort, place.description_short)) return true;
  if (!sameNullableRichText(values.descriptionLong, place.description_long)) return true;
  return false;
}

export function isEventContentDirty(
  event: EventEditor,
  values: {
    title: string;
    descriptionShort: string;
    descriptionLong: string;
    whartReviewTitle: string;
    whartReviewContent: string;
    whartElse: string;
    startDateLocal: string;
    endDateLocal: string;
    eventCategory: EventCategoryValue;
    activityType: ActivityTypeValue | "";
    isFamily: boolean;
    isRecurrent: boolean;
    pricingInfo: string;
    activitySchedule: ActivitySchedule;
  },
): boolean {
  const savedCategory = event.event_category ?? "TEMPORARY_COLLECTION";

  if (values.title !== event.title) return true;
  if (!sameNullableText(values.descriptionShort, event.description_short)) return true;
  if (!sameNullableRichText(values.descriptionLong, event.description_long)) return true;
  if (!sameNullableText(values.whartReviewTitle, event.whart_review_title)) return true;
  if (!sameNullableRichText(values.whartReviewContent, event.whart_review_content)) {
    return true;
  }
  if (!sameNullableText(values.whartElse, event.whart_else)) return true;
  if (values.eventCategory !== savedCategory) return true;
  if (!sameNullableText(values.pricingInfo, event.pricing_info)) return true;

  if (values.eventCategory === "ACTIVITY") {
    const savedActivityType = event.activity_type ?? "";
    if (values.activityType !== savedActivityType) return true;
    if (values.isFamily !== (event.is_family ?? false)) return true;
    if (values.isRecurrent !== (event.is_recurrent ?? false)) return true;

    const savedSchedule = normalizeCurrentActivitySchedule(
      values.isRecurrent,
      parseActivityScheduleFromDb(
        event.activity_schedule,
        values.isRecurrent,
      ),
    );
    const currentSchedule = normalizeCurrentActivitySchedule(
      values.isRecurrent,
      values.activitySchedule,
    );

    if (!sameActivitySchedule(currentSchedule, savedSchedule)) return true;
    return false;
  }

  if (
    values.eventCategory !== "PERMANENT_COLLECTION" &&
    savedCategory !== "PERMANENT_COLLECTION"
  ) {
    if (values.startDateLocal !== eventIsoToCalendarDay(event.start_date)) return true;
    if (values.endDateLocal !== eventIsoToCalendarDay(event.end_date)) return true;
  }

  return false;
}
